// A timeline's axis in the browser (docs/tapestry-design.md §3.5, 2b part 5): times are seconds on the group's axis (as
// the server stores them); this turns them into dates of the calendar the timeline is shown in, and picks the ticks to
// label for a zoom level. Ordinary dates use the same day counting as the server (Archives.Content.EventTime); a calendar
// of its own uses its months and day, lined up with the axis by its shared points and rate (Archives.Content.Calendar).

export type AxisCalendar = {
  title: string;
  gregorian: boolean;
  era: string | null;
  down: boolean;
  hours: number;
  minutes: number;
  months: Array<{ name: string | null; days: number }>;
  /** [this calendar's own seconds, the axis's], in order. Empty for the group's first calendar. */
  points: Array<[number, number]>;
  rate: number;
};

export type Parts = { y: number; m: number; d: number; h: number; mi: number };
export type Tick = { at: number; label: string; major: boolean };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const floorDiv = (a: number, b: number): number => Math.floor(a / b);

// Days since 1970-01-01, proleptic Gregorian (Howard Hinnant's algorithm), as the server counts them.
export function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.trunc((y >= 0 ? y : y - 399) / 400);
  const yoe = y - era * 400;
  const doy = Math.trunc((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.trunc(yoe / 4) - Math.trunc(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

export function civilFromDays(days: number): { y: number; m: number; d: number } {
  const z = days + 719468;
  const era = Math.trunc((z >= 0 ? z : z - 146096) / 146097);
  const doe = z - era * 146097;
  const yoe = Math.trunc((doe - Math.trunc(doe / 1460) + Math.trunc(doe / 36524) - Math.trunc(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.trunc(yoe / 4) - Math.trunc(yoe / 100));
  const mp = Math.trunc((5 * doy + 2) / 153);
  const d = doy - Math.trunc((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return { y: yoe + era * 400 + (m <= 2 ? 1 : 0), m, d };
}

const gregorianDays = (y: number, m: number): number =>
  m === 2 ? (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31;

// Piecewise even between shared points, at the rate outside them; no points: the same time.
function map(value: number, points: Array<[number, number]>, rate: number): number {
  if (points.length === 0) return value;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (value <= first[0]) return first[1] + (value - first[0]) * rate;
  if (value >= last[0]) return last[1] + (value - last[0]) * rate;
  let i = 0;
  while (points[i + 1]![0] < value) i++;
  const [a, b] = [points[i]!, points[i + 1]!];
  return a[1] + ((value - a[0]) * (b[1] - a[1])) / (b[0] - a[0]);
}

export class TimeAxis {
  readonly cal: AxisCalendar;
  readonly daySec: number;
  readonly yearDays: number;

  constructor(calendar: AxisCalendar | null) {
    this.cal = calendar ?? { title: "Ordinary dates", gregorian: true, era: null, down: false, hours: 24, minutes: 60, months: [], points: [], rate: 1 };
    this.daySec = this.cal.gregorian ? 86400 : this.cal.hours * this.cal.minutes * 60;
    this.yearDays = this.cal.gregorian ? 365.2425 : this.cal.months.reduce((n, m) => n + m.days, 0);
  }

  /** This calendar's own seconds on the axis, and back. */
  toAxis(local: number): number {
    return Math.round(map(local, this.cal.points, this.cal.rate));
  }

  fromAxis(axis: number): number {
    return Math.round(map(axis, this.cal.points.map(([l, a]) => [a, l] as [number, number]), 1 / this.cal.rate));
  }

  get monthCount(): number {
    return this.cal.gregorian ? 12 : this.cal.months.length;
  }

  daysIn(y: number, m: number): number {
    return this.cal.gregorian ? gregorianDays(y, m) : this.cal.months[m - 1]!.days;
  }

  get hourSec(): number {
    return this.cal.gregorian ? 3600 : this.cal.minutes * 60;
  }

  private yearStart(y: number): number {
    const yearSec = this.yearDays * this.daySec;
    return this.cal.down ? -y * yearSec : (y - 1) * yearSec;
  }

  parts(local: number): Parts {
    const seconds = local - floorDiv(local, this.daySec) * this.daySec;
    const h = Math.floor(seconds / this.hourSec);
    const mi = Math.floor((seconds - h * this.hourSec) / 60);
    if (this.cal.gregorian) {
      const { y, m, d } = civilFromDays(floorDiv(local, 86400));
      return { y, m, d, h, mi };
    }
    const yearSec = this.yearDays * this.daySec;
    const y = this.cal.down ? -floorDiv(local, yearSec) : floorDiv(local, yearSec) + 1;
    let day = Math.floor((local - this.yearStart(y)) / this.daySec);
    let m = 1;
    for (const month of this.cal.months) {
      if (day < month.days) break;
      day -= month.days;
      m++;
    }
    return { y, m: Math.min(m, this.monthCount), d: day + 1, h, mi };
  }

  compose(y: number, m: number, d: number, h = 0, mi = 0): number {
    const time = h * this.hourSec + mi * 60;
    if (this.cal.gregorian) return daysFromCivil(y, m, Math.min(d, gregorianDays(y, m))) * 86400 + time;
    const before = this.cal.months.slice(0, m - 1).reduce((n, x) => n + x.days, 0);
    return this.yearStart(y) + (before + Math.min(d, this.daysIn(y, m)) - 1) * this.daySec + time;
  }

  /** Years in the order time runs (counting down, later years are smaller). */
  order(y: number): number {
    return this.cal.down ? -y : y;
  }

  year(order: number): number {
    return this.cal.down ? -order : order;
  }

  yearText(y: number): string {
    if (this.cal.gregorian) return y > 0 ? String(y) : `${1 - y} BCE`;
    return this.cal.era ? `${y} ${this.cal.era}` : String(y);
  }

  monthText(m: number): string {
    if (this.cal.gregorian) return MONTHS[m - 1]!;
    const name = this.cal.months[m - 1]?.name;
    return name ? (name.length > 10 ? `${name.slice(0, 9)}…` : name) : `month ${m}`;
  }

  clock(h: number, mi: number): string {
    const hw = Math.max(2, String(this.cal.hours - 1).length);
    const mw = Math.max(2, String(this.cal.minutes - 1).length);
    return `${String(h).padStart(hw, "0")}:${String(mi).padStart(mw, "0")}`;
  }
}

type Unit = { kind: "year" | "month" | "day" | "hour" | "minute"; step: number };

const UNITS: Unit[] = [
  ...[1, 5, 15, 30].map((step) => ({ kind: "minute" as const, step })),
  ...[1, 3, 6, 12].map((step) => ({ kind: "hour" as const, step })),
  ...[1, 2, 5, 10].map((step) => ({ kind: "day" as const, step })),
  ...[1, 3].map((step) => ({ kind: "month" as const, step })),
  ...[1, 5, 10, 50, 100, 500, 1000, 5000, 10000].map((step) => ({ kind: "year" as const, step })),
];

function unitSeconds(axis: TimeAxis, unit: Unit): number {
  const size = { minute: 60, hour: axis.hourSec, day: axis.daySec, month: (axis.yearDays * axis.daySec) / axis.monthCount, year: axis.yearDays * axis.daySec }[unit.kind];
  return size * unit.step;
}

/**
 * The ticks to label between two axis times for a width in pixels: the finest unit whose ticks stay at least
 * <paramref name="spacing"/> pixels apart, aligned to whole years, months, days, hours or minutes of the calendar.
 */
export function ticks(axis: TimeAxis, start: number, end: number, width: number, spacing = 90): Tick[] {
  const [from, to] = [axis.fromAxis(start), axis.fromAxis(end)];
  if (!(to > from) || width <= 0) return [];
  const perPixel = (to - from) / width;
  const unit = UNITS.find((u) => unitSeconds(axis, u) / perPixel >= spacing) ?? UNITS[UNITS.length - 1]!;
  const result: Tick[] = [];
  const push = (local: number, label: string, major: boolean) => {
    if (local >= from && local <= to) result.push({ at: axis.toAxis(local), label, major });
  };
  const p = axis.parts(from);
  if (unit.kind === "year") {
    for (let t = Math.floor(axis.order(p.y) / unit.step) * unit.step, n = 0; n < 400; t += unit.step, n++) {
      const y = axis.year(t);
      const local = axis.compose(y, 1, 1);
      if (local > to) break;
      push(local, axis.yearText(y), t % (unit.step * 10) === 0);
    }
  } else if (unit.kind === "month") {
    let t = axis.order(p.y);
    let m = Math.floor((p.m - 1) / unit.step) * unit.step + 1;
    for (let n = 0; n < 400; n++) {
      const y = axis.year(t);
      const local = axis.compose(y, m, 1);
      if (local > to) break;
      push(local, m === 1 ? axis.yearText(y) : axis.monthText(m), m === 1);
      m += unit.step;
      if (m > axis.monthCount) [t, m] = [t + 1, 1];
    }
  } else if (unit.kind === "day") {
    let [t, m, d] = [axis.order(p.y), p.m, Math.floor((p.d - 1) / unit.step) * unit.step + 1];
    for (let n = 0; n < 400; n++) {
      const y = axis.year(t);
      const local = axis.compose(y, m, d);
      if (local > to) break;
      push(local, d === 1 ? `${axis.monthText(m)} ${axis.yearText(y)}` : `${d} ${axis.monthText(m)}`, d === 1);
      d += unit.step;
      // A new month starts the count again (no tick squeezed in just before the 1st).
      if (d > axis.daysIn(y, m) || (unit.step > 1 && d + unit.step / 2 > axis.daysIn(y, m))) {
        [m, d] = [m + 1, 1];
        if (m > axis.monthCount) [t, m] = [t + 1, 1];
      }
    }
  } else {
    const size = unit.kind === "hour" ? axis.hourSec : 60;
    const dayStart = axis.compose(p.y, p.m, p.d);
    for (let local = dayStart + Math.floor((from - dayStart) / (size * unit.step)) * size * unit.step, n = 0; local <= to && n < 600; local += size * unit.step, n++) {
      const q = axis.parts(local);
      const midnight = q.h === 0 && q.mi === 0;
      push(local, midnight ? `${q.d} ${axis.monthText(q.m)}` : axis.clock(q.h, q.mi), midnight);
    }
  }
  return result;
}

/** What the left edge of the view falls in, for context when zoomed in: "1998", "November 1998", "14 Nov 1998". */
export function context(axis: TimeAxis, start: number, end: number): string {
  const [from, to] = [axis.fromAxis(start), axis.fromAxis(end)];
  const p = axis.parts(from);
  const span = to - from;
  if (span > axis.yearDays * axis.daySec * 2) return "";
  if (span > (axis.yearDays * axis.daySec) / 3) return axis.yearText(p.y);
  if (span > axis.daySec * 3) return `${axis.monthText(p.m)} ${axis.yearText(p.y)}`;
  return `${p.d} ${axis.monthText(p.m)} ${axis.yearText(p.y)}`;
}
